import { MagicWandIcon, SparkleIcon, SpinnerIcon } from '@phosphor-icons/react'
import { Modal } from './Modal.tsx'
import { useAISettingsStore } from './store.ts'

interface AIGenerateModalProps {
  isOpen: boolean
  onClose: () => void
  onGenerate: (
    mode: 'new' | 'modify' | 'similar',
    message?: string,
    temperature?: number,
    topP?: number,
    model?: string,
  ) => void
  isGenerating: boolean
  onStartDJing?: () => void
}

export function AIGenerateModal({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
  onStartDJing,
}: AIGenerateModalProps) {
  const {
    temperature,
    topP,
    model,
    messageNew,
    messageModify,
    setTemperature,
    setTopP,
    setModel,
    setMessageNew,
    setMessageModify,
  } = useAISettingsStore()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="AI Loop Generator"
      icon={<SparkleIcon weight="regular" size={20} className="text-pink-500" />}
      disabled={isGenerating}
      width="w-3xl"
    >
      <div className="grid grid-cols-[2fr_1fr] gap-6">
        {/* Left column: Generation sections */}
        <div className="space-y-4">
          {/* Generate a new track */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="text-white font-semibold">Generate a new loop</div>
              <button
                onClick={() => {
                  if (messageNew.trim() && !isGenerating) {
                    onGenerate('new', messageNew.trim(), temperature, topP, model)
                  }
                }}
                disabled={isGenerating || !messageNew.trim()}
                className="px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 shrink-0 flex items-center gap-2"
              >
                {isGenerating
                  ? (
                    <>
                      <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      <span>Generating...</span>
                    </>
                  )
                  : (
                    'Generate'
                  )}
              </button>
            </div>
            <textarea
              value={messageNew}
              onChange={e => setMessageNew(e.target.value)}
              disabled={isGenerating}
              placeholder="e.g., A hypnotic techno loop with a deep bass line..."
              className="w-full h-24 px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm resize-none focus:outline-none focus:border-[#666]"
            />
          </div>

          {/* Modify this track */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="text-white font-semibold">Modify this loop</div>
              <button
                onClick={() => {
                  if (messageModify.trim() && !isGenerating) {
                    onGenerate('modify', messageModify.trim(), temperature, topP, model)
                  }
                }}
                disabled={isGenerating || !messageModify.trim()}
                className="px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 shrink-0 flex items-center gap-2"
              >
                {isGenerating
                  ? (
                    <>
                      <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      <span>Modifying...</span>
                    </>
                  )
                  : (
                    'Modify'
                  )}
              </button>
            </div>
            <textarea
              value={messageModify}
              onChange={e => setMessageModify(e.target.value)}
              disabled={isGenerating}
              placeholder="e.g., Make the bass line more aggressive and add reverb..."
              className="w-full h-24 px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm resize-none focus:outline-none focus:border-[#666]"
            />
          </div>

          {/* Generate a similar track */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="text-white font-semibold">Generate a similar loop</div>
              <button
                onClick={() => {
                  if (!isGenerating) {
                    onGenerate('similar', undefined, temperature, topP, model)
                  }
                }}
                disabled={isGenerating}
                className="px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 shrink-0 flex items-center gap-2"
              >
                {isGenerating
                  ? (
                    <>
                      <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      <span>Generating...</span>
                    </>
                  )
                  : (
                    'Generate'
                  )}
              </button>
            </div>
          </div>

          {/* Start DJing section */}
          {onStartDJing && (
            <div className="space-y-2 pt-4 border-t border-[#444]">
              <div className="flex items-center justify-between gap-4">
                <div className="text-white font-semibold">Start DJing similar</div>
                <button
                  onClick={() => {
                    if (!isGenerating && onStartDJing) {
                      onStartDJing()
                    }
                  }}
                  disabled={isGenerating}
                  className="px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-pink-500 to-fuchsia-700 text-white hover:opacity-90 disabled:opacity-50 shrink-0 flex items-center gap-2"
                >
                  <span>Start</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right column: Model parameters */}
        <div className="space-y-4">
          {/* Model selector */}
          <div className="space-y-2">
            <label className="text-white text-sm font-medium">Model</label>
            <select
              value={model}
              onChange={e => setModel(e.target.value as 'gpt-5-chat-latest' | 'chatgpt-4o-latest')}
              disabled={isGenerating}
              className="w-full px-3 py-2 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded text-white text-sm focus:outline-none focus:border-[#666]"
              style={{ colorScheme: 'dark' }}
            >
              <option value="gpt-5-chat-latest" className="bg-neutral-900 text-white">
                gpt-5-chat-latest
              </option>
              <option value="chatgpt-4o-latest" className="bg-neutral-900 text-white">
                chatgpt-4o-latest
              </option>
            </select>
          </div>

          {/* Temperature control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-sm font-medium">Temperature</label>
              <span className="text-[#888] text-sm">{temperature.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={e => setTemperature(parseFloat(e.target.value))}
              disabled={isGenerating}
              className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
            />
            <div className="flex justify-between text-xs text-[#666]">
              <span>Focused</span>
              <span>Creative</span>
            </div>
          </div>

          {/* Top P control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-white text-sm font-medium">Top P</label>
              <span className="text-[#888] text-sm">{topP.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={topP}
              onChange={e => setTopP(parseFloat(e.target.value))}
              disabled={isGenerating}
              className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
            />
            <div className="flex justify-between text-xs text-[#666]">
              <span>Narrow</span>
              <span>Broad</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
